export interface PortfolioProject {
  id: string;
  projectName: string;
  projectDescription: string;
  screenshotUrl: string;
  logoUrl?: string;
  detailContent: {
    overview: string;
    technologies: string[];
    features: string[];
    images: string[];
    liveUrl?: string;
    githubUrl?: string;
  }
}

export const portfolioProjects: PortfolioProject[] = [
  {
    id: 'osw',
    projectName: "OSW-2400",
    projectDescription: "A full-stack embedded web application for managing and monitoring a SAS switch — the hardware that routes data between tape library drives and host bus adapters. Runs on a Raspberry Pi Compute Module 5 and communicates with the expander over serial UART. Built with React, TypeScript, Material UI, and Go, replacing direct serial CLI access with a secure, role-controlled web UI for enterprise operations teams.",
    screenshotUrl: "/assets/Screenshots/OSWdashboard.png",
    logoUrl: "/assets/Logos/sllogo_black.png",
    detailContent: {
      overview: "Embedded web application for SAS switch management — real-time monitoring, zoning, OTA firmware updates, and access control for enterprise tape library environments.",
      technologies: [
        "React / TypeScript",
        "Material UI",
        "Go",
        "WebSockets",
        "JWT Auth",
        "Serial UART",
        "Embedded Linux (Raspberry Pi CM5)"
      ],
      features: [
        "Real-time PHY port status and hardware sensor monitoring via WebSockets",
        "Visual SAS zoning configuration with hardware zone application",
        "OTA firmware updates with A/B partition scheme and automatic rollback",
        "Licensing and entitlement enforcement at the PHY level",
        "CLI simulator for hardware-free integration testing"
      ],
      images: [
        "/assets/Screenshots/OSWdashboard.png",
        "/assets/Screenshots/OSWtopology.png",
        "/assets/Screenshots/OSWzoning.png",
        "/assets/Screenshots/OSWtemps.png"
      ]
    }
  },
  {
    id: 'kick-the-tires',
    projectName: "Kick the Tires",
    projectDescription: "An AI-powered Pre-Purchase Inspection platform for used car buyers. Paste a vehicle listing URL and receive a comprehensive PPI report — covering repair recommendations, common issues, market pricing, maintenance schedule, CPO eligibility, and image analysis — delivered as a PDF to your inbox. Built with Python Flask, React, Google Gemini, Crawl4AI, and Google Cloud.",
    screenshotUrl: "/assets/Screenshots/kickthetires.png",
    logoUrl: "/assets/Logos/kickthetires.ai.svg",
    detailContent: {
      overview: "Full-stack SaaS that scrapes vehicle listings, runs parallel AI analysis (Gemini), and delivers a comprehensive PPI report as a PDF via email.",
      technologies: [
        "Python Flask",
        "React / TypeScript",
        "Google Gemini / Vertex AI",
        "Crawl4AI / Playwright",
        "Google Cloud Tasks",
        "Cloud Run",
        "Firestore / GCS",
        "Stripe",
        "WeasyPrint",
        "Firebase Auth"
      ],
      features: [
        "Multi-path web scraping with anti-bot bypass",
        "Parallel AI pipeline (concurrent.futures + Gemini)",
        "Structured JSON output with schema enforcement and validation pass",
        "Server-Sent Events for real-time progress streaming",
        "PDF report generation and email delivery"
      ],
      images: [
        "/assets/Screenshots/kickthetires.png"
      ]
    }
  },
  {
    id: 'pacer-scout',
    projectName: "Pacer Scout",
    projectDescription: "At Pacer, our mission is to improve inventory acquisition for car dealerships. Pacer Scout is a live auction browser designed to let dealerships find, evaluate, and purchase inventory to meet their customers needs. The tool was written with TypeScript React, and Ant Design for the front end, and Flask and Google Firestore for the back end.",
    screenshotUrl: "/assets/Screenshots/scoutHomePage.png",
    logoUrl: "/assets/Logos/pacerscout.png",
    detailContent: {
      overview: "Full-stack auction aggregator and analytics platform for car dealerships.",
      technologies: ["React", "TypeScript", "Ant Design", "Python Flask", "Firestore", "Google Vertex AI", "Selenium", "Cloud Run"],
      features: [
        "Live auction aggregation across major platforms",
        "AI-powered Pre-Purchase Inspection (PPI) tool",
        "Advanced filtering and search",
        "Similar sold / live listing comparisons"
      ],
      images: [
        "/assets/Screenshots/scoutHomePage.png"
      ],
      liveUrl: "https://scout.pacer.co"
    }
  },
  {
    id: 'youtube-summarizer',
    projectName: "Youtube Summarizer",
    projectDescription: "This project is a simple web app I created to deliver scheduled summaries of YouTube videos via email. As an avid YouTube user, I consume much of my news via the platform. I wanted to create a tool to shorten the time it takes to consume this content, and make the delivery more facts-based and less sensational. The project was built using C# with .NET, Google Cloud Run, and React.",
    screenshotUrl: "/assets/Screenshots/summarizerScheduler.png",
    logoUrl: "/assets/Logos/youtubeSummarizerLogo.png",
    detailContent: {
      overview: "Scheduled YouTube video summarizer that delivers AI-generated summaries via email.",
      technologies: ["React", "TypeScript", "C# .NET", "Google Cloud Run", "Axios", "Firebase Auth"],
      features: [
        "Scheduled channel subscriptions",
        "AI-generated video summaries",
        "Email delivery via Postmark",
        "MCP-compatible transcript API"
      ],
      images: [
        "/assets/Screenshots/summarizerScheduler.png"
      ]
    }
  },
  {
    id: 'pacer-consumer-financing',
    projectName: "Pacer Consumer Financing",
    projectDescription: "Pacer Financing was built to help small dealerships accept financing applications directly through their websites. The suite includes an embeddable financing form and a management dashboard for handling leads and integrating with dealership DMS systems. Developed using TypeScript, React, ESBuild, and Node.js (Express), this project was one of my core responsibilities at Pacer.",
    screenshotUrl: "/assets/Screenshots/financingForm.png",
    logoUrl: "/assets/Logos/financingPacer.png",
    detailContent: {
      overview: "Embeddable financing form suite for car dealerships, with a lead management dashboard.",
      technologies: ["React", "TypeScript", "ESBuild", "Node.js / Express", "Firestore"],
      features: [
        "Embeddable financing form",
        "Secure form management system",
        "SOC2 compliant audit trail",
        "DMS integration"
      ],
      images: [
        "/assets/Screenshots/financingForm.png"
      ],
      liveUrl: "https://app.pacer.co"
    }
  },
  {
    id: 'pacer-bidr-integration',
    projectName: "Pacer BIDR Integration",
    projectDescription: "BIDR is a vehicle auction platform that allows its users to post cars for online auctions. We partnered with BIDR to offer our checkout and shipping tools through the platform. As a part of this integration, I modernized Pacer's existing checkout flow, updated styling, and added new features specific to BIDR users. The integration was built using TypeScript, React, and Node.js (Express).",
    screenshotUrl: "/assets/Screenshots/bidrHome.png",
    logoUrl: "/assets/Logos/bidrLogo.png",
    detailContent: {
      overview: "Integration of Pacer's checkout and shipping tools into the BIDR vehicle auction platform.",
      technologies: ["TypeScript", "React", "Node.js / Express", "Stripe", "Shipping API"],
      features: [
        "Modernized checkout flow",
        "Escrow payment protection",
        "Automated Bill of Sale and Odometer Disclosure generation",
        "BIDR-branded UI"
      ],
      images: [
        "/assets/Screenshots/bidrHome.png"
      ],
      liveUrl: "https://pacer.bidr.co"
    }
  }
]
